"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Modal = void 0;
var classnames_1 = require("classnames");
var PropTypes = require("prop-types");
var React = require("react");
var ariaHideOthers_1 = require("../../utils/ariaHideOthers");
var circulateTabInside_1 = require("../../utils/circulateTabInside");
var Focus = require("../../utils/focus");
var keyCodes_1 = require("../../utils/keyCodes");
var Icon_1 = require("../Icon");
var ModalBackdrop_1 = require("./ModalBackdrop");
var Modal = function (props) {
    var modalRef = React.useRef(null);
    var _a = React.useState(props.isOpen), display = _a[0], setDisplay = _a[1];
    var _b = React.useState(props.isOpen), prevDisplay = _b[0], setPrevDisplay = _b[1];
    var _c = React.useState(props.isOpen), show = _c[0], setShow = _c[1];
    // handle first load when isOpen is set to true
    React.useEffect(function () {
        var _a;
        var currentMod = modalRef.current;
        if (props.isOpen) {
            document.body.classList.add('modal-open');
            if ((_a = props.onOpenFocusTo) === null || _a === void 0 ? void 0 : _a.current) {
                props.onOpenFocusTo.current.focus();
            }
            else {
                currentMod === null || currentMod === void 0 ? void 0 : currentMod.focus();
            }
            Focus.saveFocus(props.restoreFocusTo);
            if (props.ariaHide && currentMod) {
                (0, ariaHideOthers_1.ariaHideOthers)(currentMod);
            }
            // turn the modal on
            setPrevDisplay(true);
            setDisplay(true);
            setShow(true);
        }
        return function () {
            document.body.classList.remove('modal-open');
            Focus.restoreFocus();
            // Remove aria-hidden on sibling elements up to body.
            if (props.ariaHide && currentMod) {
                (0, ariaHideOthers_1.ariaRevertOthers)(currentMod);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    React.useEffect(function () {
        var _a, _b;
        // open the modal
        if (props.isOpen && !display) {
            // save focus of passed through element if provided
            if (props.restoreFocusTo) {
                // save focus of passed through element if provided
                Focus.saveFocus(props.restoreFocusTo);
            }
            else {
                // save focus to the element that activated the modal
                Focus.saveFocus(document.activeElement);
            }
            document.body.classList.add('modal-open');
            if (props.ariaHide && modalRef.current) {
                (0, ariaHideOthers_1.ariaHideOthers)(modalRef.current);
            }
            setDisplay(true);
            setShow(true);
        }
        // close the modal
        if (!props.isOpen && display) {
            document.body.classList.remove('modal-open');
            // When closed, return focus to the element that triggered opening
            Focus.restoreFocus();
            // Remove aria-hidden on sibling elements up to body.
            if (props.ariaHide && modalRef.current) {
                (0, ariaHideOthers_1.ariaRevertOthers)(modalRef.current);
            }
            setDisplay(false);
            setShow(false);
        }
        // set focus to the element once the element has finished rendering
        if (display && !prevDisplay) {
            if ((_a = props.onOpenFocusTo) === null || _a === void 0 ? void 0 : _a.current) {
                props.onOpenFocusTo.current.focus();
            }
            else {
                (_b = modalRef.current) === null || _b === void 0 ? void 0 : _b.focus();
            }
            setPrevDisplay(display);
        }
        // return focus from the dialog
        if (!display && prevDisplay) {
            // When closed, return focus to the element that triggered opening
            Focus.restoreFocus();
            setPrevDisplay(display);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.isOpen, props.restoreFocusTo, props.ariaHide, display, show, modalRef, props.onOpenFocusTo]);
    /**
     * When user clicks on backdrop/overlay
     */
    var clickBackdrop = function (event) {
        if (event.target === event.currentTarget && !!props.closeOnClick) {
            props.closeModal(event);
        }
    };
    /**
     * The keydown handler is used for detecting escape press, tabbing,
     * and shift-tabbing (reverse tab). The alternative keyPress handler
     * wasn't useful at reverse tabbing.
     */
    var keyDownHandler = function (event) {
        // If modal is open and escape was pressed, close the modal
        if (props.isOpen && event.keyCode === keyCodes_1.KeyCodes.ESCAPE && !!props.closeOnClick) {
            props.closeModal(event);
        }
        if (!(modalRef === null || modalRef === void 0 ? void 0 : modalRef.current) || !props.isOpen) {
            return;
        }
        (0, circulateTabInside_1.circulateTabInside)(modalRef.current, event);
    };
    var classes = (0, classnames_1.default)('modal', 'hc-modal', {
        'd-block': display && !props.isCentered,
        'd-none': !display,
        'modal--centered': display && props.isCentered,
        'show': show,
    });
    var attributes = {
        'aria-hidden': props.isOpen ? false : true,
        'aria-labelledby': props.ariaLabelledBy || props.id + "-heading",
        'aria-modal': true,
        className: classes,
        id: props.id,
        onClick: clickBackdrop,
        onKeyDown: keyDownHandler,
        ref: modalRef,
        role: 'dialog',
        tabIndex: -1,
    };
    return (React.createElement(React.Fragment, null,
        React.createElement("div", __assign({}, attributes),
            React.createElement("div", { className: "modal-dialog col-10 col-xs-12 col-sm-6", role: "document" },
                React.createElement("div", { className: (0, classnames_1.default)('modal-container', props.className) },
                    React.createElement("div", { className: "modal-content" },
                        React.createElement("div", { className: "modal-body" }, props.children),
                        React.createElement("div", { className: "modal-close" },
                            React.createElement(Icon_1.Icon, { screenreader: "Close", onClick: props.closeModal, type: "css", symbol: "controlClose24" }))),
                    React.createElement("span", { className: "sr-only" }, "End of dialog")))),
        React.createElement(ModalBackdrop_1.ModalBackdrop, { isOpen: props.isOpen })));
};
exports.Modal = Modal;
/* tslint:disable:no-string-literal */
exports.Modal['defaultProps'] = {
    ariaHide: true,
    closeOnClick: true,
    isOpen: false,
};
exports.Modal['propTypes'] = {
    ariaHide: PropTypes.bool,
    ariaLabelledBy: PropTypes.string,
    children: PropTypes.element.isRequired,
    className: PropTypes.string,
    closeModal: PropTypes.func.isRequired,
    closeOnClick: PropTypes.bool,
    id: PropTypes.string.isRequired,
    isCentered: PropTypes.bool,
    isOpen: PropTypes.bool.isRequired,
    onOpenFocusTo: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({ current: PropTypes.object }),
    ]),
    restoreFocusTo: PropTypes.element,
};
/* tslint:enable:no-string-literal */
